import type { RecordView } from '@kit/sync/types/record-view';
import { isLowStock } from '@/entities/item/helpers/is-low-stock';
import { nextDue } from '@/entities/item/helpers/next-due';
import type { Item } from '@/entities/item/schemas/item';
import type { Category } from '@/entities/shared/category/category';
import { matchesQuery } from '@/shared/lib/text/matches-query';
import type { InventoryFilter, InventorySort } from '../schemas/inventory-filter';

type ItemCriteria = {
  readonly query: string;
  readonly category: Category | '';
  readonly location: string;
  readonly filter: InventoryFilter;
  readonly sort: InventorySort;
};

function byName(a: RecordView<Item>, b: RecordView<Item>): number {
  return a.data.name.localeCompare(b.data.name, 'es');
}

export function filterItems(items: readonly RecordView<Item>[], criteria: ItemCriteria): RecordView<Item>[] {
  const rows = items.filter(function keep({ data }) {
    const urgency = nextDue(data)?.urgency;
    if (criteria.category && data.category !== criteria.category) return false;
    if (criteria.location && data.location !== criteria.location) return false;
    if (criteria.filter === 'attention' && urgency !== 'soon') return false;
    if (criteria.filter === 'expired' && urgency !== 'overdue') return false;
    if (criteria.filter === 'low' && !isLowStock(data)) return false;
    return matchesQuery(
      criteria.query,
      data.name,
      data.brand,
      data.location,
      data.store,
      data.notes,
      data.active_ingredient,
      data.tags,
    );
  });
  if (criteria.sort === 'name') return rows.sort(byName);
  if (criteria.sort === 'location') {
    return rows.sort(
      (a, b) => (a.data.location ?? '~').localeCompare(b.data.location ?? '~', 'es') || byName(a, b),
    );
  }
  return rows.sort(
    (a, b) =>
      (nextDue(a.data)?.date ?? '9999').localeCompare(nextDue(b.data)?.date ?? '9999') || byName(a, b),
  );
}
